import { useEffect, useState } from 'react';
import { isMobile, isTablet, isDesktop } from '~/utils/helper';

interface DeviceType {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    windowWidth: number;
}

/**
 * Custom hook to detect device type and window dimensions
 * @returns DeviceType object with device detection flags and window width
 */
export const useDeviceType = (): DeviceType => {
    const [deviceType, setDeviceType] = useState<DeviceType>({
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        windowWidth: 1024 // Default to desktop width for SSR
    });

    useEffect(() => {
        const updateDeviceType = () => {
            const windowWidth = window.innerWidth;

            setDeviceType({
                isMobile: isMobile(windowWidth),
                isTablet: isTablet(),
                isDesktop: isDesktop(windowWidth),
                windowWidth
            });
        };

        // Initial check
        updateDeviceType();

        // Add event listener for window resize
        window.addEventListener('resize', updateDeviceType);

        // Cleanup
        return () => {
            window.removeEventListener('resize', updateDeviceType);
        };
    }, []);

    return deviceType;
};

/**
 * Simple hook to check if device is mobile
 * @returns boolean - true if mobile device
 */
export const useIsMobile = (): boolean => {
    const { isMobile } = useDeviceType();
    return isMobile;
};

/**
 * Simple hook to check if device is tablet
 * @returns boolean - true if tablet device
 */
export const useIsTablet = (): boolean => {
    const { isTablet } = useDeviceType();
    return isTablet;
};

/**
 * Simple hook to check if device is desktop
 * @returns boolean - true if desktop device
 */
export const useIsDesktop = (): boolean => {
    const { isDesktop } = useDeviceType();
    return isDesktop;
};

export default useDeviceType;
